import { resolveNrcProximity, type NrcProximityInput } from '../reportSourceEvidence';
/**
 * buildCanonicalFactStore — assemble the single authoritative fact store.
 *
 * Pipeline:
 *   1. Run the EXISTING `buildAllRegistryResolvers(ctx)` manifest, which
 *      already tier-reconciles power / cooling / COD / financial assumptions.
 *      Flatten its `resolveAll()` output into a `resolved.<registryId>.<key>`
 *      lookup so descriptors can read it without re-implementing the ladder.
 *   2. For each `FactDescriptor`, gather the PRESENT candidate slots
 *      (dot-path resolution against `{ sed, report, providerData, resolved }`
 *      plus the declarative siteContext CLAIM candidates), turn them into
 *      `TierSource[]` via `tierSource(...)`, and reconcile them with the
 *      EXISTING `reconcileTierSources` (tier-1 wins; conflicts detected).
 *   3. Derive an honest `FactStatus` from the WINNER's declared `kind` + tier
 *      + the SED coverage mode — NOT from the blanket tier-1 `'provider'`
 *      stamp the generic SED walker applies (the provenance-laundering
 *      guard). A winning claim candidate may stamp its own status (developer
 *      claims are `'labeled-unverifiable'`).
 *   4. Surface every conflict (and downgrade tier-1-vs-tier-1 disagreements to
 *      `'estimated'`) into `unresolvedConflicts` — never silently drop one.
 *
 * SCORING SLICE — `buildScoringFactSlice({ sed, report })` resolves ONLY the
 * `SCORING_FACT_IDS` subset against a `{ sed, report }` root (no registry
 * manifest / site entity needed), so the portfolio pillar scorer consumes the
 * SAME descriptor table + reconciliation the prompt layer uses — one routing
 * table, two projections; the two layers cannot drift.
 *
 * PURE. No I/O, no Firestore, no DOM. The full store is constructed ONLY when
 * `reportGroundingMode() !== 'off'` (prompt path); the scoring slice is pure
 * and flag-independent (it feeds pillar scores, not prompt bytes).
 */

import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { studyAreaGrossAcres } from '../studyAreaGrossAcres';
import { computeDataCenterFinance } from '../dataCenterFinance';
import { dataCenterFinancialModelV1Schema } from '../../types/dataCenterFinancial';
import {
  PHASE_POWER_COMPONENT_LABEL,
  type ProjectIntent,
  type SiteRagReport,
} from '../../types/siteRag';
// Declared-band fill: `resolveIntentPowerStackBand` is the existing SSOT for
// turning a bucket intent into a band (it returns null for an exact figure,
// which is exactly the gate the fill needs). Never re-derive from
// `bucketMwRange` — its maxima are inclusive while `bucketFromMw` is
// exclusive-upper, so a governed exact 350 would re-derive 200–500.
import { resolveIntentPowerStackBand } from '../powerStack';
import type { ExtractedClaim } from '../../types/siteContext';
import { siteContextForEligibleDocuments } from '../evidenceDocEligibility';
import type { ConflictDetail, ResolverTier, SourceRef, TierSource } from '../resolvers/types';
import { buildAllRegistryResolvers, type SiteResolverContext } from '../resolvers/registryManifest';
import { reconcileTierSources, tierSource } from '../resolvers/resolveCore';
import { estimateIxLatencyMs, IX_LATENCY_MS_PER_KM } from '../ixLatency';
// Unit corroboration reuses the ingestion coercion SSOT (CLAUDE.md §4 risk
// class) rather than re-implementing unit sniffing here.
import {
  convertToCanonical,
  sniffUnitAdjacentInSpan,
} from '../../services/contextIngestion/coercions';
import {
  assessPhaseFeasibility,
  firmCapacitySiteContextFromIntent,
} from '../phaseFeasibility';
import { assessDeclaredIntentFirmCapacityN1 } from '../phaseContingency';
import { phaseFacilityLoadMW } from '../powerStack';
import { phaseIncrementMW } from '../projectIntent';
import {
  createCanonicalFactStore,
  type CanonicalFact,
  type CanonicalFactStore,
  type FactStatus,
  type NumericGroundingScope,
} from './canonicalFacts';
import {
  FACT_DESCRIPTORS,
  SCORING_FACT_IDS,
  US_PROVIDER_DEPENDENT_FACT_IDS,
  type ClaimCandidate,
  type FactCandidate,
  type FactDescriptor,
  type TextCandidate,
} from './factDescriptors';

export interface BuildCanonicalFactStoreArgs {
  /** The resolver context bag (drives `buildAllRegistryResolvers`). */
  ctx: SiteResolverContext;
  /** The SED whose slots back the descriptor dot-paths. */
  sed: SiteEvidenceDocument;
  /** Optional live provider aggregate (reserved; descriptors may read it). */
  providerData?: Record<string, unknown>;
  reportType: string;
  /** Coverage mode override; defaults to `sed.coverageMode ?? 'US'`. */
  coverageMode?: SiteEvidenceDocument['coverageMode'];
  /** Deterministic clock for tests. */
  nowIso?: string;
  /**
   * The SELECTED report type's authored typed preferences (2026-08-13,
   * Redhead RCA) — exposed on the resolution root as `prefs` so descriptors
   * can source report-setup values (e.g. `prefs.firmCapacityBasisMW`). The
   * wizard's authored firm-capacity basis never reached the fact store and
   * the grounding gate blocked the report's own configuration as invented.
   */
  typedPrefsForType?: Record<string, unknown>;
}

/** `SourceRef.kind`s that, at tier-1, count as a genuine authoritative source. */
const VERIFIED_TIER1_KINDS: ReadonlySet<SourceRef['kind']> = new Set([
  'provider',
  'user-amendment',
  'manual',
  'report-override',
  'site-canonical',
]);

/** Read a dot-path against the resolution root. Returns `undefined` when absent. */
function readPath(root: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = root;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

/**
 * Flatten the registry manifest's `resolveAll()` outputs into a single
 * `resolved.<registryId>.<fieldKey>` → resolvedValue lookup, so a descriptor
 * path like `resolved.projectIntent.powerRequirementMW` reads the already-
 * tier-reconciled value.
 */
function buildResolvedLookup(ctx: SiteResolverContext): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const resolver of buildAllRegistryResolvers(ctx)) {
    const bucket = (out[resolver.registryId] ??= {});
    for (const [fieldKey, resolution] of Object.entries(resolver.resolveAll())) {
      if (resolution.resolvedValue !== undefined && resolution.resolvedValue !== null) {
        bucket[fieldKey] = resolution.resolvedValue;
      }
    }
  }
  return out;
}

/* ── X-SED-6: siteContext claim resolution (conservative) ─────────────── */

/**
 * The LIVE claims visible to the resolution root: the report's frozen
 * `siteContextSnapshot` when a report is in scope (scoring path —
 * reproducible against the claims the report was generated with), else the
 * SED's own `siteContext`. Mis-extracted claims never compete.
 */
function liveClaimsFromRoot(root: Record<string, unknown>): ExtractedClaim[] {
  const report = root.report as
    | { siteContextSnapshot?: { claims?: unknown } }
    | undefined;
  const sed = root.sed as SiteEvidenceDocument | undefined;
  // A saved report's own snapshot remains its historical scoring authority.
  // For current SED reads, the frozen included-document list is the same
  // authority already used by sedToText. Retained excluded claims are audit
  // history, not fresh numeric evidence. Legacy SEDs without a list retain
  // their existing behaviour; an explicit empty list excludes document claims.
  const context = sed && Array.isArray(sed.uploadedDocuments)
    ? siteContextForEligibleDocuments(sed?.siteContext, new Set(sed.uploadedDocuments.flatMap(document =>
      typeof document.docId === 'string' && document.docId.length > 0 ? [document.docId] : [],
    ))) : sed?.siteContext;
  const raw = report?.siteContextSnapshot?.claims ?? context?.claims;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (c): c is ExtractedClaim =>
      !!c && typeof c === 'object' && (c as { state?: string }).state !== 'mis-extracted',
  );
}

/** Eligibility-filtered current SED claims, shared with frozen numeric roles. */
export function liveSiteContextClaims(sed: SiteEvidenceDocument): ExtractedClaim[] {
  return liveClaimsFromRoot({ sed });
}

/** Strip separators + parse a decimal ("1,100" → 1100). Null when non-finite. */
function parseNum(raw: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Format a range bound for the display band string (thousands-separated). */
function formatBound(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString('en-US') : String(n);
}

/** `low–high` band anywhere in the text (en/em dash, hyphen, or "to"). */
const NUMERIC_RANGE_RE = /(\d[\d,]*(?:\.\d+)?)\s*(?:[–—-]|to\s)\s*(\d[\d,]*(?:\.\d+)?)/;

function parseNumericRange(text: string): { low: number; high: number } | null {
  const m = text.match(NUMERIC_RANGE_RE);
  if (!m) return null;
  const low = parseNum(m[1]!);
  const high = parseNum(m[2]!);
  if (low === null || high === null || low >= high) return null;
  return { low, high };
}

/* ── Unit corroboration for a bare-number `claimedValue` ──────────────── */

/**
 * A `claimedValue` is a BARE number — the extractor strips whatever unit the
 * sentence stated. Several claim candidates carry a UNIT-ANCHORED
 * `valuePattern` (`/(\d[\d,]*(?:\.\d+)?)\s*MW/i`) precisely to guarantee the
 * number they lift is expressed in the FACT's unit. Adopting `claimedValue`
 * verbatim bypassed that guarantee: a claim whose value is the kV, km, kW or
 * MVA aspect of the same sentence was published as N MW of grid headroom,
 * and — because these candidates stamp `'labeled-unverifiable'` — surfaced
 * as an unverified user claim of a number nobody ever made.
 *
 * So a bare `claimedValue` may only win a unit-anchored candidate when it is
 * CORROBORATED:
 *   (a) the `valuePattern` captures that same number somewhere in the claim
 *       text (claim + `sourceSpan`) — the number is provably in-unit, or
 *   (b) {@link sniffUnitAdjacentInSpan} finds a unit token directly after
 *       that number in the claim text whose canonical form (via
 *       {@link convertToCanonical}) is one the pattern anchors — so "0.05 GW"
 *       corroborates an MW fact and contributes 50, and "40 ha" corroborates
 *       an acres fact and contributes 98.84. Read from the span, never
 *       fabricated.
 * Uncorroborated, the `claimedValue` shortcut is REFUSED and extraction falls
 * back to the pattern itself, which is in-unit by construction. When the
 * pattern finds nothing either, the claim contributes nothing and the fact
 * stays absent (or resolves from a lower tier) — never a wrong assertion.
 */

/**
 * Raw unit tokens {@link sniffUnitAdjacentInSpan} can return, probed against
 * a candidate's `valuePattern` to learn which CANONICAL units that pattern
 * anchors. Probing the pattern (rather than parsing its source) keeps the
 * pattern itself the single source of truth for the fact's unit.
 */
const UNIT_PROBE_TOKENS = ['%', 'GW', 'MW', 'kW', 'MVA', 'ha', 'hectares', 'acres', 'ac'] as const;

/** Probe magnitude — arbitrary; only the unit token decides a probe match. */
const UNIT_PROBE_VALUE = 1234;

interface UnitAnchor {
  /** The pattern needs MORE than a bare number to match — it is anchored. */
  anchored: boolean;
  /** Canonical units the pattern accepts; empty when none are sniffable. */
  canonicalUnits: ReadonlySet<string>;
}

/** Descriptor patterns are module constants, so the probe result is cached. */
const UNIT_ANCHOR_CACHE = new WeakMap<RegExp, UnitAnchor>();

/** Copy `re` without the stateful `g`/`y` flags (safe for a one-shot test). */
function statelessCopy(re: RegExp): RegExp {
  return new RegExp(re.source, re.flags.replace(/[gy]/g, ''));
}

/** Copy `re` as a GLOBAL matcher (safe for an `exec` scan). */
function globalCopy(re: RegExp): RegExp {
  return new RegExp(re.source, `${re.flags.replace(/[gy]/g, '')}g`);
}

/** Tolerance for "this is the same number" (mirrors the coercion sniffer). */
function numericTolerance(value: number): number {
  return Math.max(1e-9, Math.abs(value) * 1e-6);
}

/** Is `valuePattern` unit-anchored, and which canonical units does it take? */
function unitAnchorOf(valuePattern: RegExp): UnitAnchor {
  const cached = UNIT_ANCHOR_CACHE.get(valuePattern);
  if (cached) return cached;
  const probe = statelessCopy(valuePattern);
  // A pattern that already matches a BARE number anchors nothing: the
  // `claimedValue` shortcut keeps its existing (unguarded) behaviour.
  const anchored = !probe.test(String(UNIT_PROBE_VALUE));
  const canonicalUnits = new Set<string>();
  if (anchored) {
    for (const token of UNIT_PROBE_TOKENS) {
      if (
        !probe.test(`${UNIT_PROBE_VALUE} ${token}`)
        && !probe.test(`${UNIT_PROBE_VALUE}${token}`)
      ) continue;
      const canonical = convertToCanonical(UNIT_PROBE_VALUE, token).unit;
      if (canonical) canonicalUnits.add(canonical);
    }
  }
  const anchor: UnitAnchor = { anchored, canonicalUnits };
  UNIT_ANCHOR_CACHE.set(valuePattern, anchor);
  return anchor;
}

/** Does `valuePattern` capture exactly `value` ANYWHERE in `text`? */
function patternCapturesValue(value: number, text: string, valuePattern: RegExp): boolean {
  const re = globalCopy(valuePattern);
  const tol = numericTolerance(value);
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m[0] === '') re.lastIndex += 1; // zero-length match guard
    const captured = m[1];
    if (captured === undefined) continue;
    const n = parseNum(captured);
    if (n !== null && Math.abs(n - value) <= tol) return true;
  }
  return false;
}

/**
 * Corroborate a bare-number `claimedValue` against a candidate's
 * `valuePattern`. Returns the value to adopt (converted to the fact's
 * canonical unit when the span states a convertible one), or `null` when the
 * number is not corroborated and must not win the fact.
 */
function corroborateClaimedNumber(
  value: number,
  text: string,
  valuePattern: RegExp,
): number | null {
  const anchor = unitAnchorOf(valuePattern);
  if (!anchor.anchored) return value;
  if (patternCapturesValue(value, text, valuePattern)) return value;
  const token = sniffUnitAdjacentInSpan(value, text);
  if (token === undefined) return null;
  const canonical = convertToCanonical(value, token);
  if (!canonical.unit || !anchor.canonicalUnits.has(canonical.unit)) return null;
  return canonical.value;
}

/** The bare number a `claimedValue` carries (number, or numeric string). */
function bareNumberFromClaimedValue(cv: unknown): number | null {
  if (typeof cv === 'number' && Number.isFinite(cv)) return cv;
  if (typeof cv === 'string') {
    const clean = cv.trim().replace(/,/g, '');
    if (/^-?\d+(?:\.\d+)?$/.test(clean)) return parseNum(clean);
  }
  return null;
}

/**
 * Pull a numeric value from a claim: `claimedValue` first (gated by unit
 * corroboration when the candidate declares a unit-anchored `valuePattern`),
 * then the text.
 */
function numericFromClaim(
  claim: ExtractedClaim,
  text: string,
  valuePattern?: RegExp,
): number | null {
  const bare = bareNumberFromClaimedValue(claim.claimedValue);
  if (bare !== null) {
    if (!valuePattern) return bare;
    const corroborated = corroborateClaimedNumber(bare, text, valuePattern);
    if (corroborated !== null) return corroborated;
    // Uncorroborated: refuse the shortcut, fall through to the pattern.
  }
  const m = valuePattern ? text.match(valuePattern) : text.match(/(\d[\d,]*(?:\.\d+)?)/);
  if (m?.[1]) return parseNum(m[1]);
  return null;
}

/** The extraction result of one matched claim candidate. */
interface ClaimExtraction {
  value: unknown;
  numericValues?: number[];
}

/**
 * Resolve ONE claim candidate against the live claims. Conservative: the
 * claim's explicit sedField must match when the candidate declares fields;
 * category remains the fallback for legacy claims without a field and
 * category-only candidates. The text discriminator must still hit.
 */
function resolveClaimCandidate(
  claims: readonly ExtractedClaim[],
  spec: ClaimCandidate,
): ClaimExtraction | null {
  for (const claim of claims) {
    const category = String(claim.category ?? '');
    const sedField = typeof claim.sedField === 'string' ? claim.sedField : undefined;
    const categoryHit = spec.categories.includes(category);
    const sedFieldHit = !!spec.sedFields && !!sedField && spec.sedFields.includes(sedField);
    if (spec.sedFields && sedField !== undefined ? !sedFieldHit : !categoryHit) continue;

    const text = [claim.claim, claim.sourceSpan]
      .filter((s): s is string => typeof s === 'string')
      .join(' ');
    if (spec.textMatch && !spec.textMatch.test(text)) continue;
    if (spec.textExclude && spec.textExclude.test(text)) continue;

    if (spec.extract === 'text') {
      const v = claim.claimedValue;
      if (typeof v === 'string' && v.trim() !== '') return { value: v.trim() };
      continue;
    }

    // X-SED-7 — value embedded in the claim PROSE (first defined capture of
    // `valuePattern`), for facts whose `claimedValue` carries a different
    // aspect (the pipeline claim's claimedValue is the OPERATOR).
    if (spec.extract === 'text-pattern') {
      const m = spec.valuePattern ? text.match(spec.valuePattern) : null;
      const captured = m?.slice(1).find((g): g is string => g !== undefined);
      if (captured && captured.trim() !== '') return { value: captured.trim() };
      continue;
    }

    if (spec.extract === 'numeric-range') {
      const rangeSource = [
        typeof claim.claimedValue === 'string' ? claim.claimedValue : '',
        text,
      ].join(' ');
      // X-SED-7 — a `rangePattern` anchors the band to its shape (e.g. the
      // `$…M` cost band) so unrelated in-text number pairs ("phase 2-3",
      // "230 kV") can never be read as the range.
      const anchored = spec.rangePattern ? rangeSource.match(spec.rangePattern)?.[0] : undefined;
      const range = parseNumericRange(
        spec.rangePattern ? anchored ?? '' : rangeSource,
      );
      if (range) {
        return {
          // Display band string (en dash — house numeric-range style); the
          // numeric BOUNDS feed the validator allow-set via `numericValues`.
          value: `${formatBound(range.low)}–${formatBound(range.high)}`,
          numericValues: [range.low, range.high],
        };
      }
      // No band in the text — degrade to a single numeric value.
    }

    const n = numericFromClaim(claim, text, spec.valuePattern);
    if (n !== null) return { value: n };
  }
  return null;
}

/* ── X-SED-7: SED text-pool resolution (last-resort, conservative) ─────── */

/** Pool-size guards so a pathological doc set cannot blow up the scan. */
const TEXT_POOL_MAX_ITEMS = 60;
const TEXT_POOL_MAX_CHARS = 400_000;

/**
 * Flatten ONE text-pool path value into scannable strings. A STRING pool is
 * scanned directly; an ARRAY pool contributes each item that is a string or
 * an object carrying a `textContent` / `text` string field (the
 * uploaded-document shape). Anything else contributes nothing.
 */
function textPoolStrings(poolValue: unknown): string[] {
  if (typeof poolValue === 'string') return poolValue ? [poolValue] : [];
  if (!Array.isArray(poolValue)) return [];
  const out: string[] = [];
  for (const item of poolValue.slice(0, TEXT_POOL_MAX_ITEMS)) {
    if (typeof item === 'string') {
      if (item) out.push(item);
      continue;
    }
    if (item && typeof item === 'object') {
      const o = item as { textContent?: unknown; text?: unknown };
      if (typeof o.textContent === 'string' && o.textContent) out.push(o.textContent);
      else if (typeof o.text === 'string' && o.text) out.push(o.text);
    }
  }
  return out;
}

/**
 * Resolve ONE text candidate against its declared pools, in path order. The
 * FIRST DEFINED capture group of the first hit wins (so the curated
 * `siteContext.text` brief outranks raw uploaded-doc text by ordering).
 * Numeric extraction is comma-stripped + plausibility-banded; out-of-band
 * or non-finite captures contribute nothing.
 */
function resolveTextCandidate(
  root: Record<string, unknown>,
  spec: TextCandidate,
): ClaimExtraction | null {
  for (const path of spec.paths) {
    for (const pool of textPoolStrings(readPath(root, path))) {
      const m = pool.slice(0, TEXT_POOL_MAX_CHARS).match(spec.pattern);
      if (!m) continue;
      const captured = m.slice(1).find((g): g is string => g !== undefined);
      if (!captured || captured.trim() === '') continue;
      if (spec.extract === 'text') return { value: captured.trim() };
      const n = parseNum(captured);
      if (n === null) continue;
      if (spec.min !== undefined && n < spec.min) continue;
      if (spec.max !== undefined && n > spec.max) continue;
      return { value: n };
    }
  }
  return null;
}

/* ── Status derivation ─────────────────────────────────────────────────── */

/**
 * Derive the honest `FactStatus` for a resolved fact.
 *
 * - tier-1 disagreement (conflict at tier-1) → `'estimated'` (sources
 *   disagree; do not assert one as fact). This wins over the verified path.
 * - degraded coverage AND a US-provider-dependent fact → `'labeled-
 *   unverifiable'` (the laundering guard).
 * - tier-1 winner with a genuine authoritative `kind` → `'verified'`.
 * - everything else with a present value → `'estimated'`.
 * - no value → `'absent'`.
 */
function deriveStatus(args: {
  factId: string;
  winner?: SourceRef;
  winnerTier: ResolverTier | null;
  hasValue: boolean;
  hasTier1Conflict: boolean;
  coverageMode: CanonicalFactStore['coverageMode'];
}): FactStatus {
  if (!args.hasValue || !args.winner || args.winnerTier == null) return 'absent';

  const degraded = args.coverageMode !== 'US';
  if (degraded && US_PROVIDER_DEPENDENT_FACT_IDS.has(args.factId)) {
    // On a non-US / territory-degraded site the US-only provider was skipped;
    // any value present came from a non-authoritative path. Never `verified`.
    return 'labeled-unverifiable';
  }

  if (args.hasTier1Conflict) return 'estimated';

  if (args.winnerTier === 1 && VERIFIED_TIER1_KINDS.has(args.winner.kind)) {
    return 'verified';
  }
  return 'estimated';
}

/* ── Descriptor resolution ─────────────────────────────────────────────── */

/**
 * DC-REG-003 — Distinguish nearby off-site transmission/substation pins from
 * committed interconnection, capacity allocation, or low-side delivery voltage.
 * A nearby third-party KMZ pin (`distanceKm > 0.5`, or `committed === false`,
 * or an uncommitted pin when a utility study specifies a distinct requested
 * delivery voltage such as 34.5 kV) must never be promoted as Tier-1 committed
 * `grid.voltageKv` (`Interconnection voltage`).
 */
export interface VoltageProvenanceLedger {
  nearbyInfrastructureVoltagesKv: Array<{
    voltageKv: number;
    name?: string;
    distanceKm?: number;
    sourceRef: string;
    isCommittedConnection: false;
    isCommittedAllocation: false;
    isDeliveryVoltage: false;
  }>;
  requestedDeliveryVoltageKv: number | null;
  requestedDeliverySource: {
    docId?: string;
    docName?: string;
    pageNumber?: number;
    excerpt?: string;
  } | null;
  committedConnectionVoltageKv: number | null;
  committedAllocationMw: number | null;
  externalBinaryUtilityOracleStatus: 'AVAILABLE' | 'BLOCKED';
}

export function resolveVoltageProvenanceLedger(
  root: Record<string, unknown>,
  options?: { hasLocalBinaryUtilityPdf?: boolean },
): VoltageProvenanceLedger {
  const nearbyInfrastructureVoltagesKv: VoltageProvenanceLedger['nearbyInfrastructureVoltagesKv'] = [];
  const substationsRaw =
    readPath(root, 'sed.onSiteInfrastructure.data.substations') ??
    readPath(root, 'onSiteInfrastructure.data.substations');
  const substations = Array.isArray(substationsRaw) ? substationsRaw : [];
  for (let i = 0; i < substations.length; i++) {
    const sub = substations[i] as Record<string, unknown> | undefined;
    if (!sub || typeof sub !== 'object') continue;
    const kv = typeof sub.voltageKv === 'number' && Number.isFinite(sub.voltageKv)
      ? sub.voltageKv
      : parseVoltageKv(sub.name ?? sub.label);
    const distKm = typeof sub.distanceKm === 'number' && Number.isFinite(sub.distanceKm)
      ? sub.distanceKm
      : undefined;
    const isCommitted = sub.committed === true || sub.isCommittedInterconnection === true;
    if (kv !== null && !isCommitted) {
      nearbyInfrastructureVoltagesKv.push({
        voltageKv: kv,
        name: typeof sub.name === 'string' ? sub.name : typeof sub.label === 'string' ? sub.label : undefined,
        distanceKm: distKm,
        sourceRef: `sed:onSiteInfrastructure.substations.${i}`,
        isCommittedConnection: false,
        isCommittedAllocation: false,
        isDeliveryVoltage: false,
      });
    }
  }

  let requestedDeliveryVoltageKv: number | null = null;
  let requestedDeliverySource: VoltageProvenanceLedger['requestedDeliverySource'] = null;
  const directDeliveryKv =
    readPath(root, 'sed.interconnection.data.requestedDeliveryVoltageKv') ??
    readPath(root, 'sed.interconnection.data.deliveryVoltageKv') ??
    readPath(root, 'sed.grid.data.requestedDeliveryVoltageKv') ??
    readPath(root, 'sed.grid.data.deliveryVoltageKv');
  if (typeof directDeliveryKv === 'number' && Number.isFinite(directDeliveryKv) && directDeliveryKv > 0) {
    requestedDeliveryVoltageKv = directDeliveryKv;
  }

  const docsCandidate =
    readPath(root, 'documents') ??
    readPath(root, 'source.documents') ??
    readPath(root, 'sed.uploadedDocuments');
  const docsList = Array.isArray(docsCandidate) ? docsCandidate : [];
  const sisDeliveryRe = /\b(?:Developer\s+)?requested\s+service\s+at\s+(\d+(?:\.\d+)?)\s*kV\b|\blow\s+voltage\s*:\s*(\d+(?:\.\d+)?)\s*kV\b|\b(\d+(?:\.\d+)?)\s*kV\s+low[- ]side\s+delivery\b/i;
  for (const rawDoc of docsList) {
    if (!rawDoc || typeof rawDoc !== 'object') continue;
    const d = rawDoc as Record<string, unknown>;
    const docId = typeof d.id === 'string' ? d.id : undefined;
    const docName = typeof d.name === 'string' ? d.name : typeof d.fileName === 'string' ? d.fileName : undefined;
    const fullExtracted = typeof d.fullExtractedText === 'string' ? d.fullExtractedText : '';
    const pages = Array.isArray(d.pages) ? d.pages : [];
    for (const p of pages) {
      if (!p || typeof p !== 'object') continue;
      const pageObj = p as Record<string, unknown>;
      const pageText =
        typeof pageObj.text === 'string' && pageObj.text.length > 0
          ? pageObj.text
          : typeof pageObj.charStart === 'number' &&
              typeof pageObj.charEnd === 'number' &&
              fullExtracted.length >= pageObj.charEnd
            ? fullExtracted.slice(pageObj.charStart, pageObj.charEnd)
            : '';
      const pageNumber =
        typeof pageObj.pageNumber === 'number'
          ? pageObj.pageNumber
          : typeof pageObj.page === 'number'
            ? pageObj.page
            : undefined;
      const m = pageText.match(sisDeliveryRe);
      if (m) {
        const parsedKv = Number(m[1] ?? m[2] ?? m[3]);
        if (Number.isFinite(parsedKv) && parsedKv > 0) {
          requestedDeliveryVoltageKv = parsedKv;
          requestedDeliverySource = {
            docId,
            docName,
            pageNumber,
            excerpt: m[0],
          };
          break;
        }
      }
    }
    if (requestedDeliverySource) break;
    const fullText =
      fullExtracted.length > 0
        ? fullExtracted
        : typeof d.textContent === 'string'
          ? d.textContent
          : typeof d.text === 'string'
            ? d.text
            : '';
    const m = fullText.match(sisDeliveryRe);
    if (m) {
      const parsedKv = Number(m[1] ?? m[2] ?? m[3]);
      if (Number.isFinite(parsedKv) && parsedKv > 0) {
        requestedDeliveryVoltageKv = parsedKv;
        requestedDeliverySource = {
          docId,
          docName,
          excerpt: m[0],
        };
        break;
      }
    }
  }

  let committedConnectionVoltageKv: number | null = null;
  const iaStatus = readPath(root, 'sed.interconnection.data.interconnectionAgreementStatus');
  const isExecutedIa = typeof iaStatus === 'string' && /\b(executed|signed|active|committed)\b/i.test(iaStatus) && !/\b(none|draft|pending|unexecuted|study)\b/i.test(iaStatus);
  if (isExecutedIa) {
    const committedKv = readPath(root, 'sed.interconnection.data.committedVoltageKv');
    if (typeof committedKv === 'number' && Number.isFinite(committedKv) && committedKv > 0) {
      committedConnectionVoltageKv = committedKv;
    }
  }

  const committedAllocRaw = readPath(root, 'sed.interconnection.data.committedCapacityMw');
  const committedAllocationMw =
    isExecutedIa && typeof committedAllocRaw === 'number' && Number.isFinite(committedAllocRaw) && committedAllocRaw > 0
      ? committedAllocRaw
      : null;

  return {
    nearbyInfrastructureVoltagesKv,
    requestedDeliveryVoltageKv,
    requestedDeliverySource,
    committedConnectionVoltageKv,
    committedAllocationMw,
    externalBinaryUtilityOracleStatus: options?.hasLocalBinaryUtilityPdf === true ? 'AVAILABLE' : 'BLOCKED',
  };
}

export function isOffSiteOrUncommittedSubstationSlot(
  sub: unknown,
  root: Record<string, unknown>,
): boolean {
  if (!sub || typeof sub !== 'object') return true;
  const s = sub as Record<string, unknown>;
  if (s.isNearbyInfrastructureOnly === true || s.committed === false || s.isCommittedInterconnection === false) {
    return true;
  }
  const isExplicitlyCommitted =
    s.committed === true ||
    s.isCommittedInterconnection === true ||
    s.role === 'poi' ||
    s.role === 'committed-interconnection';
  const distKm = typeof s.distanceKm === 'number' && Number.isFinite(s.distanceKm) ? s.distanceKm : null;
  if (distKm !== null && distKm > 0.5 && !isExplicitlyCommitted) {
    return true;
  }
  const subKv = typeof s.voltageKv === 'number' && Number.isFinite(s.voltageKv) ? s.voltageKv : null;
  if (subKv !== null && !isExplicitlyCommitted) {
    const ledger = resolveVoltageProvenanceLedger(root);
    if (ledger.requestedDeliveryVoltageKv !== null && ledger.requestedDeliveryVoltageKv !== subKv) {
      return true;
    }
  }
  return false;
}

/** True when the slot candidate's guards mark the present value ABSENT. */
function candidateGuardedAbsent(
  candidate: FactCandidate,
  value: unknown,
  root: Record<string, unknown>,
): boolean {
  if (
    candidate.absentValues &&
    typeof value === 'string' &&
    candidate.absentValues.some((a) => a.toLowerCase() === value.trim().toLowerCase())
  ) {
    return true;
  }
  if (candidate.absentWhen) {
    const guardValue = readPath(root, candidate.absentWhen.path);
    if (candidate.absentWhen.equals.some((e) => e === guardValue)) return true;
  }
  return false;
}

/** Resolve ONE descriptor into a `CanonicalFact`. */
function resolveDescriptor(
  descriptor: FactDescriptor,
  root: Record<string, unknown>,
  coverageMode: CanonicalFactStore['coverageMode'],
  nowIso: string,
): CanonicalFact {
  const sources: TierSource[] = [];
  // Status overrides are shared across ALL candidate kinds: a slot candidate
  // can also stamp its own status (e.g. a structured utility-letter figure
  // resolving tier-1 as labeled-unverifiable, never as verified).
  const statusOverridesByRef = new Map<string, FactStatus>();
  const numericValuesByRef = new Map<string, number[]>();
  const selectedGrossAcres = studyAreaGrossAcres((root.sed as SiteEvidenceDocument)?.studyAreaScope);
  const selectedAreaFact = descriptor.factId === 'siteArea.acres' || descriptor.factId === 'siteArea.boundaryAcres';
  for (const candidate of descriptor.candidates as readonly FactCandidate[]) {
    // Parcel/profile totals describe other geometry; do not let those aliases
    // replace the explicitly selected scope or create a false area conflict.
    if (selectedAreaFact && selectedGrossAcres !== undefined && candidate.path !== 'sed.studyAreaScope.grossAreaAcres') continue;
    if (candidate.path === 'sed.studyAreaScope.grossAreaAcres' && selectedGrossAcres === undefined) continue;
    // NRC returns 999 for an empty 200-km catalog search. Keep it out of
    // canonical facts/numeric allow-sets as well as the human prose. Guard the
    // provider candidate only; explicit human amendments keep their authority.
    if (candidate.path === 'sed.nuclearInfra.data.nearestReactorDistKm') {
      const nuclear = readPath(root, 'sed.nuclearInfra.data') as NrcProximityInput | undefined;
      if (!nuclear || resolveNrcProximity(nuclear).distanceKm == null) continue;
    }
    // DC-REG-003 — nearby off-site KMZ pins (distanceKm > 0.5) or uncommitted pins
    // conflicting with a utility SIS requested delivery voltage must not be promoted
    // as Tier-1 committed `grid.voltageKv` (`Interconnection voltage`).
    if (candidate.path === 'sed.onSiteInfrastructure.data.substations.0.voltageKv') {
      const sub0 = readPath(root, 'sed.onSiteInfrastructure.data.substations.0');
      if (isOffSiteOrUncommittedSubstationSlot(sub0, root)) continue;
    }
    const value = readPath(root, candidate.path);
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (candidateGuardedAbsent(candidate, value, root)) continue;
    sources.push(
      tierSource(candidate.tier, value, {
        kind: candidate.kind,
        ref: candidate.ref,
        label: candidate.label,
      }),
    );
    if (candidate.statusWhenWinner) statusOverridesByRef.set(candidate.ref, candidate.statusWhenWinner);
  }

  // X-SED-6 — claim-derived candidates (conservative matching). A winning
  // claim can stamp its own status (developer claims are labeled-unverifiable)
  // and carry range BOUNDS for the validator allow-set.
  if (descriptor.claimCandidates && descriptor.claimCandidates.length > 0) {
    const claims = liveClaimsFromRoot(root);
    if (claims.length > 0) {
      for (const spec of descriptor.claimCandidates) {
        const extraction = resolveClaimCandidate(claims, spec);
        if (!extraction) continue;
        sources.push(
          tierSource(spec.tier, extraction.value, {
            kind: spec.kind,
            ref: spec.ref,
            label: spec.label,
          }),
        );
        if (spec.statusWhenWinner) statusOverridesByRef.set(spec.ref, spec.statusWhenWinner);
        if (extraction.numericValues) numericValuesByRef.set(spec.ref, extraction.numericValues);
      }
    }
  }

  // X-SED-7 — text-derived candidates (siteContext.text / uploaded docs),
  // the last-resort rung for doc-established facts the claim extractor
  // missed. Same reconciliation + status-override machinery as claims; a
  // resolved slot or claim always outranks them by tier.
  if (descriptor.textCandidates && descriptor.textCandidates.length > 0) {
    for (const spec of descriptor.textCandidates) {
      const extraction = resolveTextCandidate(root, spec);
      if (!extraction) continue;
      sources.push(
        tierSource(spec.tier, extraction.value, {
          kind: spec.kind,
          ref: spec.ref,
          label: spec.label,
        }),
      );
      if (spec.statusWhenWinner) statusOverridesByRef.set(spec.ref, spec.statusWhenWinner);
      if (extraction.numericValues) numericValuesByRef.set(spec.ref, extraction.numericValues);
    }
  }

  const resolution = reconcileTierSources(descriptor.factId, sources, nowIso);
  const hasValue = resolution.resolvedValue !== undefined && resolution.resolvedValue !== null;
  const winner = resolution.sources[0];
  const winnerTier: ResolverTier | null = hasValue ? resolution.resolvedFromTier : null;

  // A tier-1-vs-tier-1 disagreement is the dangerous class: two authoritative
  // sources (e.g. parcel.acres vs siteProfile.acres) report different values.
  const hasTier1Conflict =
    winnerTier === 1 &&
    (resolution.conflicts ?? []).some((c) => c.tier === 1);

  let status = deriveStatus({
    factId: descriptor.factId,
    winner,
    winnerTier,
    hasValue,
    hasTier1Conflict,
    coverageMode,
  });
  // Winning-claim status override (e.g. developer-stated ⇒ labeled-
  // unverifiable). Never resurrects an absent fact; for the facts the
  // degraded-coverage guard governs it produces the same grade anyway.
  if (status !== 'absent' && winner) {
    const override = statusOverridesByRef.get(winner.ref);
    if (override) status = override;
  }

  const winnerNumericValues = winner ? numericValuesByRef.get(winner.ref) : undefined;

  return {
    factId: descriptor.factId,
    label: descriptor.label,
    value: hasValue ? resolution.resolvedValue : null,
    unit: descriptor.unit,
    status,
    resolvedFromTier: winnerTier,
    sources: resolution.sources,
    conflicts: resolution.conflicts,
    basis: descriptor.basis,
    valueKind: descriptor.valueKind,
    ...(winnerNumericValues ? { numericValues: winnerNumericValues } : {}),
  };
}

/* ── Derived facts ─────────────────────────────────────────────────────── */

/**
 * Build DERIVED canonical facts — values NOT carried in a single SED slot but
 * COMPUTED deterministically from resolved/SED inputs, using the SAME formula
 * the deterministic datasheet uses (single source of truth for the derivation),
 * so the governed narrative value can never contradict the rendered metric.
 *
 * Derived facts are always `'estimated'` (a model proxy, never a measurement)
 * and are only emitted when their input is present — an absent input yields no
 * fact (not an `'absent'` line), since a derived value has no "ground truth"
 * slot to declare NOT ESTABLISHED against.
 */
function buildDerivedFacts(root: Record<string, unknown>): CanonicalFact[] {
  const out: CanonicalFact[] = [];

  // IX latency — round-trip ms estimated from the nearest-IX fibre distance via
  // `estimateIxLatencyMs` (the SAME helper `buildInfrastructureFromSed` uses for
  // `datasets.infrastructure.latency`). Fixes the live Redhead defect where the
  // metric read "10.245 ms" but the narrative said "over 2 ms".
  const distKm = readPath(root, 'sed.connectivity.data.nearestIxDistKm');
  const distKmNum = readPath(root, 'sed.connectivity.data.nearestIxKind') !== 'colo-fallback' && typeof distKm === 'number' ? distKm : null;
  const latencyMs = estimateIxLatencyMs(distKmNum);
  if (latencyMs != null && distKmNum != null) {
    out.push({
      factId: 'ix.latencyMs',
      label: 'IX latency (round-trip est.)',
      value: latencyMs,
      unit: 'ms',
      status: 'estimated',
      resolvedFromTier: 2,
      sources: [
        {
          tier: 2,
          kind: 'site-context',
          ref: 'derived:ixLatency(nearestIxDistKm)',
          label: `Derived: ${distKmNum} km × ${IX_LATENCY_MS_PER_KM} ms/km`,
          value: latencyMs,
        },
      ],
      basis: 'Geographic IX proximity heuristic; not measured latency or a surveyed fiber route',
      valueKind: 'numeric',
    });
  }

  /*
   * Phase-feasibility MW — deterministic outputs from the SAME
   * `assessPhaseFeasibility` / `phaseFacilityLoadMW` helpers that build the
   * report's FIRM-CAPACITY FEASIBILITY block and structured deep-dive rows.
   *
   * These are intentionally separate facts rather than aliases of
   * `firmCapacity.targetMw`: a user-authored accreditation TARGET and a
   * planning-default per-phase calculation are different quantities.  Each
   * fact carries a numeric grounding scope so the validator only admits it
   * when the report names the matching phase + metric.  Generic prose such as
   * "a firm 49 MW of accredited capacity" therefore still cannot borrow a
   * nearby phase-derived value.
   */
  const intent = readPath(root, 'intent') as ProjectIntent | undefined;
  if (intent && Array.isArray(intent.phases)) {
    const phases = intent.phases.slice().sort((a, b) => a.sequence - b.sequence);
    const siteContext = firmCapacitySiteContextFromIntent(intent);
    for (const [phaseIndex, phase] of phases.entries()) {
      for (const [metric, value] of [['cumulative', phase.capacityMW], ['addition', phaseIncrementMW(phases, phaseIndex)]] as const) {
        if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) continue;
        out.push({
          factId: `phaseCapacity.${phase.sequence}.${metric}Mw`,
          label: `Phase ${phase.sequence} planned ${metric} capacity`,
          value, unit: 'MW', status: 'verified', resolvedFromTier: 1,
          sources: [{ tier: 1, kind: 'manual', ref: `intent:phases.${phase.id}.capacityMW`,
            label: 'User-declared phase plan, not committed supply', value }],
          basis: `Planned ${metric} capacity; delivery and load boundary require separate evidence`,
          valueKind: 'numeric',
          numericGroundingScope: { kind: 'phase-capacity', phaseSequence: phase.sequence, metric,
            ...(intent.fieldSource?.powerBasis !== 'user-unknown' && intent.powerBasis
              ? { powerBasis: intent.powerBasis } : {}) },
        });
      }
      const feasibility = assessPhaseFeasibility(phase, siteContext);
      if (!feasibility) continue;

      const phaseSequence = phase.sequence;
      const phaseLabel = phase.label?.trim() || `Phase ${phaseSequence}`;
      // Stable fact-id segment. Phase ids are UUID-like but may contain user
      // imported punctuation; keep the canonical id token-safe.
      const phaseKey = String(phase.id || `sequence-${phaseSequence}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || `sequence-${phaseSequence}`;
      const source = {
        tier: 2 as const,
        kind: 'site-context' as const,
        ref: `derived:phaseFeasibility(${phase.id || phaseSequence})`,
        label: `Derived from Project Intent phase ${phaseSequence} using planning-default ELCC`,
      };
      const push = (args: {
        suffix: string;
        label: string;
        value: number | null;
        metric: Extract<NumericGroundingScope, { kind: 'phase-feasibility' }>['metric'];
        componentLabel?: string;
      }): void => {
        if (typeof args.value !== 'number' || !Number.isFinite(args.value)) return;
        // The report block and structured rows render these values with the
        // shared one-decimal contract. Store that same deterministic display
        // precision so floating-point tails (402.49999999999994) never create
        // an artificial mismatch against the emitted 402.5 MW.
        const value = round1(args.value);
        out.push({
          factId: `phaseFeasibility.${phaseKey}.${args.suffix}`,
          label: args.label,
          value,
          unit: 'MW',
          status: 'estimated',
          resolvedFromTier: 2,
          sources: [{ ...source, value }],
          basis: 'Project Intent + planning-default ELCC / reserve-margin arithmetic',
          valueKind: 'numeric',
          numericGroundingScope: {
            kind: 'phase-feasibility',
            phaseSequence,
            phaseLabel,
            metric: args.metric,
            ...(args.componentLabel ? { componentLabel: args.componentLabel } : {}),
          },
        });
      };

      push({
        suffix: 'facilityLoadMw',
        label: `Phase ${phaseSequence} facility load`,
        // Do not relabel the grader's raw/sizing-basis load as a FACILITY load
        // when the power-basis resolver cannot produce that layer. The prompt
        // says "facility load TBD" in that case, so the canonical store must
        // remain equally honest.
        value: phaseFacilityLoadMW(phase, intent),
        metric: 'facility-load',
      });
      push({
        suffix: 'requiredFirmMw',
        label: `Phase ${phaseSequence} required firm capacity`,
        value: feasibility.requiredFirmMW,
        metric: 'required-firm',
      });
      push({
        suffix: 'firmDeliverableMw',
        label: `Phase ${phaseSequence} firm-deliverable capacity`,
        value: feasibility.firmDeliverableMW,
        metric: 'firm-deliverable',
      });
      push({
        suffix: 'firmMarginMw',
        label: `Phase ${phaseSequence} firm-capacity margin`,
        value: typeof feasibility.firmDeliverableMW === 'number' && typeof feasibility.requiredFirmMW === 'number'
          ? round1(feasibility.firmDeliverableMW - feasibility.requiredFirmMW)
          : null,
        metric: 'firm-margin',
      });

      for (const perSource of feasibility.perSource) {
        const componentLabel = PHASE_POWER_COMPONENT_LABEL[perSource.component];
        const componentKey = perSource.component.replace(/[^a-z0-9]+/g, '-');
        push({
          suffix: `source.${componentKey}.capacityMw`,
          label: `Phase ${phaseSequence} ${componentLabel} nameplate`,
          value: perSource.capacityMW,
          metric: 'source-capacity',
          componentLabel,
        });
        push({
          suffix: `source.${componentKey}.firmMw`,
          label: `Phase ${phaseSequence} ${componentLabel} firm contribution`,
          value: perSource.firmMW,
          metric: 'source-firm',
          componentLabel,
        });
      }
    }

    /*
     * Report-level N-1 fields. The shared calculator only resolves the final
     * phase when every contingency/source group is explicitly declared with
     * exact MW. It never borrows `technology.typicalModuleMWe` or trusts the
     * generated report's own arithmetic.
     */
    const n1 = assessDeclaredIntentFirmCapacityN1(intent);
    if (n1) {
      const n1Source = {
        tier: 2 as const,
        kind: 'site-context' as const,
        ref: `derived:assessDeclaredIntentFirmCapacityN1(${n1.phaseId})`,
        label: `Derived from ${n1.phaseLabel} exact declared source groups using planning-default ELCC`,
      };
      const pushN1 = (args: {
        factId: string;
        label: string;
        value: number;
        metric: Extract<NumericGroundingScope, { kind: 'firm-capacity-n1' }>['metric'];
      }): void => {
        out.push({
          factId: args.factId,
          label: args.label,
          value: args.value,
          unit: 'MW',
          status: 'estimated',
          resolvedFromTier: 2,
          sources: [{ ...n1Source, value: args.value }],
          basis: `${n1.phaseLabel}; one explicitly declared source group per contingency; planning-default ELCC`,
          valueKind: 'numeric',
          numericGroundingScope: {
            kind: 'firm-capacity-n1',
            metric: args.metric,
          },
        });
      };

      pushN1({
        factId: 'firmCapacityN1.largestUnitMW',
        label: 'N-1 largest declared source-group contingency',
        value: n1.largestContingencyMW,
        metric: 'largest-contingency',
      });
      pushN1({
        factId: 'firmCapacityN1.firmDeliverableN1MW',
        label: 'N-1 remaining firm-deliverable capacity',
        value: n1.firmDeliverableN1MW,
        metric: 'firm-deliverable-n1',
      });
      pushN1({
        factId: 'firmCapacityN1.requiredFirmMW',
        label: 'N-1 reserve-padded firm requirement',
        value: n1.requiredFirmMW,
        metric: 'required-firm-n1',
      });
      pushN1({
        factId: 'firmCapacityN1.shortfallMW',
        label: 'N-1 firm-capacity shortfall',
        value: n1.shortfallMW,
        metric: 'shortfall-n1',
      });
    }
  }

  return out;
}

/** Round to 1 decimal (queue years / parsed figures). */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Parse an interconnection-queue duration out of the free-text
 * `grid.estimatedQueueTime` slot ("3-5 years", "24–36 months", "4 years").
 * A stated range resolves to its MIDPOINT (documented assumption — balanced
 * between the optimistic low-end read the old prose regex took and a
 * worst-case high-end read). Returns years, or null when nothing parseable /
 * outside the engineering-plausible band (0.05–40 y).
 */
function parseQueueTimeYears(raw: unknown): { years: number; source: string } | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const m = raw.match(
    /(\d+(?:\.\d+)?)\s*(?:(?:[–—-]|to)\s*(\d+(?:\.\d+)?))?\s*(months?|mos?\b|years?|yrs?\b)/i,
  );
  if (!m) return null;
  const lo = parseNum(m[1]!);
  const hi = m[2] ? parseNum(m[2]) : null;
  if (lo === null) return null;
  const mid = hi !== null && hi > lo ? (lo + hi) / 2 : lo;
  const years = /^mo/i.test(m[3]!) ? mid / 12 : mid;
  if (!Number.isFinite(years) || years < 0.05 || years > 40) return null;
  return { years: round1(years), source: raw.trim() };
}

/**
 * Parse the interconnection voltage (kV) out of the free-text `grid.voltage`
 * slot ("230 kV", "230/69 kV", "115kV"). Multi-voltage strings resolve to the
 * HIGHEST figure (the transmission-side interconnection voltage). Guarded to
 * the engineering-plausible 1–1500 kV band.
 */
function parseVoltageKv(raw: unknown): number | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  // `grid.voltage` / `nearestTransmissionVoltage` are DEDICATED voltage slots
  // (schema), so every in-band number in them is a voltage ("230/69 kV" →
  // 230 and 69; the substation's transmission side is the max).
  const pool = [...raw.matchAll(/(\d[\d,]*(?:\.\d+)?)/g)]
    .map((m) => parseNum(m[1]!))
    .filter((n): n is number => n !== null && n >= 1 && n <= 1500);
  if (pool.length === 0) return null;
  return Math.max(...pool);
}

/**
 * DECLARED-BAND FILL — the governed fact for a range-bucket project.
 *
 * THE BUG THIS FIXES (2026-08-14, Triple Threat). When the intent declares a
 * BUCKET rather than an exact figure, `powerRequirementMW` is deliberately
 * unset ("a range never reconciles into a scalar", projectIntent.ts) and the
 * SED's scalar power slots stay null BY INVARIANT (siteEvidence.ts). So
 * `power.declaredMw` resolved ABSENT, and the report's faithful echo of the
 * user's OWN declaration — "a power requirement under 10 MW" — was graded an
 * invented number. Two blocking violations on correct prose, on every
 * bucket-intent report the platform produces.
 *
 * WHY A SINGLE POINT, NOT AN INTERVAL. "Under 10 MW" establishes an upper
 * BOUND; it does not evidence any particular figure beneath it. Modelling the
 * bucket as a containment interval was measured to ground "A 12,000 MW
 * gigacampus" (on `over-1500mw`), "The 500 MW campus" (on `200-500mw`, whose
 * table max is inclusive) and "a firm 49 MW of accredited capacity" (on
 * `10-50mw`) — it converts a bound into an allow-set with infinitely many
 * members and guts the anti-hallucination pins. One point, the documented
 * sizing basis, is what the user actually declared.
 *
 * Prose that quotes the band FAITHFULLY still grounds without any new arm:
 * `RANGE_LOW_RE` tags "10-50 MW" as {value:50, rangeLow:10} and the validator's
 * existing stated-range arm matches any fact inside it. The report may quote
 * its band; it may not invent a point inside it.
 *
 * Shape follows the established X-SED-6 band idiom: `value` is the human band
 * STRING, the numbers ride in `numericValues`. The string carries NO unit — the
 * prompt's canonical block appends it (`formatCanonicalValue`), so "up to
 * 10 MW" would render "up to 10 MW MW".
 *
 * FILL-WHEN-ABSENT only, so a tier-2 site-context or tier-3 inferred scalar
 * still wins, and it runs BEFORE `applyUserAmendmentFacts` so an operator's
 * answer overrides it.
 */
function applyDeclaredBandFact(
  facts: CanonicalFact[],
  sed: SiteEvidenceDocument | undefined,
  intent: ProjectIntent | undefined,
): void {
  const idx = facts.findIndex((f) => f.factId === 'power.declaredMw');
  if (idx < 0 || facts[idx]!.status !== 'absent') return;

  // Persisted band first (written by the intent→SED stamp); fall back to
  // resolving it from the intent for SEDs that predate bucket bands. The
  // resolver returns null for an exact figure, which is the gate we want.
  const stamped = sed?.power?.data?.bucketBand ?? null;
  const resolved = stamped ? null : resolveIntentPowerStackBand(intent);
  const sizingBasisMw = stamped?.sizingBasisMw ?? resolved?.sizingBasisMW;
  if (typeof sizingBasisMw !== 'number' || !Number.isFinite(sizingBasisMw) || sizingBasisMw <= 0) return;

  /*
   * Build the display from the band's STRUCTURE, never by string-munging its
   * label. `POWER_BUCKET_LABEL` is presentation text and does not have a
   * uniform shape: the legacy `over-200mw` label is "200 MW+ (legacy)", which
   * a trailing-unit strip leaves intact — measured, that rendered "200 MW+
   * (legacy) MW" in the generation prompt AND leaked the internal "(legacy)"
   * token into the block message and the repair prompt.
   *
   * Open edges arrive as `null` (never 0 / Infinity), which is what
   * distinguishes "up to 10" from "1500 or more".
   */
  const low = stamped ? stamped.enteredMw.low : resolved?.openLow ? null : resolved?.enteredBandMW.min ?? null;
  const high = stamped ? stamped.enteredMw.high : resolved?.openHigh ? null : resolved?.enteredBandMW.max ?? null;
  const num = (n: number): string => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(1))));
  const display = low === null && high !== null ? `up to ${num(high)}`
    : high === null && low !== null ? `${num(low)} or more`
    : low !== null && high !== null ? `${num(low)}–${num(high)}`
    : null;
  if (!display) return;

  const prev = facts[idx]!;
  facts[idx] = {
    factId: prev.factId,
    label: prev.label,
    unit: prev.unit,
    valueKind: prev.valueKind,
    basis: prev.basis,
    value: display,
    numericValues: [sizingBasisMw],
    bandDisplay: `${display} MW`,
    // The user DECLARED this band — a tier-1 manual entry, not an estimate.
    // 'estimated' would raise an unlabeled-estimate warn on every in-band
    // mention (147 of them live); 'labeled-unverifiable' is upgraded warn→block
    // in on-mode, trading two blocks for a storm.
    status: 'verified',
    resolvedFromTier: 1,
    sources: [{
      tier: 1,
      kind: 'manual',
      ref: 'intent:powerRequirement(bucket)',
      label: `User project intent — declared range ${display} MW`,
      value: display,
    }],
  };
}

/**
 * Fibre corridor facts from the user's CAD-derived infrastructure layers
 * (2026-08-17).
 *
 * The narrative routinely quotes two fibre quantities that had NO governing
 * fact: the length of a planned fibre EXTENSION drawn by the user ("an 8.52 km
 * fiber extension is required") and the distance to the nearest existing
 * long-haul corridor ("a long-haul fiber corridor is 6.18 km away"). Both live
 * in `onSiteInfrastructure.data.corridors` (kind: 'fiber'), measured from the
 * user's own CAD geometry — real, precise values the store simply never
 * surfaced. Ungoverned, they blocked as invented while all four "nearest X"
 * distance facts were absent (12 of 18 live blocks, 2026-08-17) and remain
 * advisory noise now that other km facts resolve.
 *
 * The cure is the `siteArea.boundaryAcres` pattern: ADD the fact the product
 * itself displays. User-drawn geometry is authoritative for what the user
 * plans, so — like the boundary polygon — these resolve 'verified'.
 *
 * Extension vs corridor is decided by LABEL (/extension|ext\.?/i): a corridor
 * the user names an extension is a planned build whose relevant figure is its
 * LENGTH; anything else is existing infrastructure whose relevant figure is
 * its DISTANCE from the parcel. Additive only — no existing fact, precedence
 * or scoring input changes.
 */
function applyFiberCorridorFacts(
  facts: CanonicalFact[],
  sed: SiteEvidenceDocument | undefined,
): void {
  const corridors = (sed as unknown as {
    onSiteInfrastructure?: { data?: { corridors?: ReadonlyArray<{
      label?: string; kind?: string; lengthKm?: number; distanceFromParcelKm?: number;
    }> } };
  })?.onSiteInfrastructure?.data?.corridors ?? [];
  const fiber = corridors.filter((c) => c?.kind === 'fiber');
  if (fiber.length === 0) return;

  const round2 = (n: number): number => Number(n.toFixed(2));
  const isExtension = (c: { label?: string }): boolean => /extension|\bext\b\.?/i.test(c.label ?? '');

  const pushIfAbsent = (fact: CanonicalFact): void => {
    if (facts.some((f) => f.factId === fact.factId && f.status !== 'absent')) return;
    const idx = facts.findIndex((f) => f.factId === fact.factId);
    if (idx >= 0) facts[idx] = fact;
    else facts.push(fact);
  };

  const extensions = fiber
    .filter((c) => isExtension(c) && typeof c.lengthKm === 'number' && Number.isFinite(c.lengthKm))
    .sort((a, b) => (a.lengthKm ?? 0) - (b.lengthKm ?? 0));
  if (extensions.length > 0) {
    // Shortest planned extension: the narrative's "N km extension is required"
    // is the minimum build that reaches the corridor.
    const value = round2(extensions[0]!.lengthKm!);
    pushIfAbsent({
      factId: 'fiber.extensionLengthKm',
      label: 'Planned fibre extension (length)',
      unit: 'km',
      valueKind: 'numeric',
      value,
      status: 'verified',
      resolvedFromTier: 1,
      sources: [{
        tier: 1,
        kind: 'manual',
        ref: 'layers:onSiteInfrastructure.corridors(fiber-extension)',
        label: `User CAD layer "${extensions[0]!.label ?? 'Fiber Extension'}"`,
        value,
      }],
    } as unknown as CanonicalFact);
  }

  /*
   * EXISTING requires a POSITIVE marker (adversarial review, 2026-08-17). The
   * first cut treated "everything not labelled an extension" as existing
   * infrastructure, so a user-drawn "Proposed Fiber Route" became a VERIFIED
   * "nearest existing fibre corridor" at ~0 km — false-present evidence for a
   * planned build. A corridor is EXISTING only when its label says so
   * (existing / long-haul / backbone / trunk) and never when it reads as
   * planned; anything ambiguous is skipped — conservative-absent.
   */
  const EXISTING_RE = /existing|long[- ]?haul|backbone|trunk/i;
  const PLANNED_RE = /propos|planned|future|\broute\b|new\s+build/i;
  const existing = fiber
    .filter((c) => !isExtension(c)
      && EXISTING_RE.test(c.label ?? '')
      && !PLANNED_RE.test(c.label ?? '')
      && typeof c.distanceFromParcelKm === 'number' && Number.isFinite(c.distanceFromParcelKm))
    .sort((a, b) => (a.distanceFromParcelKm ?? 0) - (b.distanceFromParcelKm ?? 0));
  if (existing.length > 0) {
    const value = round2(existing[0]!.distanceFromParcelKm!);
    pushIfAbsent({
      factId: 'fiber.corridorDistanceKm',
      label: 'Nearest existing fibre corridor',
      unit: 'km',
      valueKind: 'numeric',
      value,
      status: 'verified',
      resolvedFromTier: 1,
      sources: [{
        tier: 1,
        kind: 'manual',
        ref: 'layers:onSiteInfrastructure.corridors(fiber)',
        label: `User CAD layer "${existing[0]!.label ?? 'Fiber corridor'}"`,
        value,
      }],
    } as unknown as CanonicalFact);
  }
}

/**
 * DERIVED FILLS for the scoring-critical facts whose SED slots need a unit
 * conversion / string parse the pure dot-path table cannot express:
 *
 *   - `grid.queueYears`  ← `interconnection.queueDurationP50Days` (÷ 365) or
 *                          the free-text `grid.estimatedQueueTime` parse,
 *                          ONLY with explicit site-load-service basis.
 *   - `wind.resourceCapacityFactorPct` ← saved wind-resource fraction × 100
 *                          (grounding only, absent from SCORING_FACT_IDS).
 *   - `grid.voltageKv`   ← the free-text `grid.voltage` (or
 *                          `nearestTransmissionVoltage`) parse, highest kV.
 *
 * Fill-when-absent ONLY: a slot- or claim-resolved fact always wins; the
 * derived value replaces the fact IN PLACE (same array position) so the
 * canonical block ordering is stable. Derived fills are `'estimated'`.
 */
function applyScoringDerivedFacts(
  facts: CanonicalFact[],
  root: Record<string, unknown>,
  scope: { reportType?: string; siteId?: string; ownerUid?: string },
): void {
  const replace = (
    factId: string,
    next: Omit<CanonicalFact, 'factId' | 'label' | 'unit' | 'valueKind'>,
  ): void => {
    const idx = facts.findIndex((f) => f.factId === factId);
    if (idx < 0 || facts[idx]!.status !== 'absent') return;
    const prev = facts[idx]!;
    facts[idx] = {
      factId: prev.factId,
      label: prev.label,
      unit: prev.unit,
      valueKind: prev.valueKind,
      ...next,
    };
  };

  // The data-center writer deliberately leaves generation/PPA scalar slots
  // empty. Its project CAPEX belongs only to financial scope and comes from
  // the same schema-checked annual engine, never the BOM subtotal or a quote.
  const assumptions = readPath(root, 'sed.financialAssumptions');
  if (scope.reportType === 'financial'
    && readPath(root, 'sed.financialAssumptions.businessModelKind') === 'data-center-capacity-fee') {
    const parsed = dataCenterFinancialModelV1Schema.safeParse(readPath(root, 'sed.financialAssumptions.dataCenterModel'));
    if (parsed.success) {
      const model = parsed.data;
      const result = computeDataCenterFinance(model);
      const report = root.report as SiteRagReport | undefined;
      const sed = root.sed as SiteEvidenceDocument;
      const basis = sed.demonstrationProvenance;
      const frozen = report?.financialAnalysis?.dataCenter;
      const frozenAssumptions = report?.financialAssumptionsAtGen;
      const frozenResult = frozen ? computeDataCenterFinance(frozen.model) : undefined;
      const frozenAssumptionsResult = frozenAssumptions
        ? computeDataCenterFinance(frozenAssumptions.dataCenterModel) : undefined;
      const identityMatches = scope.siteId === model.siteId
        && (assumptions as { siteId?: string; currency?: string }).siteId === model.siteId
        && (assumptions as { currency?: string }).currency === model.currency
        && (!scope.ownerUid || scope.ownerUid === model.ownerUid)
        && (!basis || basis.ownerUid === model.ownerUid)
        && (model.authority !== 'fictional-scenario'
          || (basis?.scenarioId === model.scenarioId && basis?.ownerUid === model.ownerUid))
        && (!report || (report.reportType === 'financial' && report.siteId === model.siteId
          && (!report.sedId || report.sedId === sed.id)
          && (!report._ownerUid || report._ownerUid === model.ownerUid)
          && (!report.demonstrationProvenance || (report.demonstrationProvenance.ownerUid === model.ownerUid
            && report.demonstrationProvenance.scenarioId === model.scenarioId))
          && (!report.financialAnalysis || report.financialAnalysis.modelKind === 'data-center-capacity-fee')
          && (!report.financialAnalysis?.decisionScope
            || report.financialAnalysis.decisionScope.scenarioId === model.scenarioId)))
        && (!frozenAssumptions || (frozenAssumptions.businessModelKind === 'data-center-capacity-fee'
          && frozenAssumptions.siteId === model.siteId
          && frozenAssumptionsResult?.computable === true
          && frozenAssumptionsResult.modelHash === result.modelHash))
        && (!frozen || (report?.financialAnalysis?.modelKind === 'data-center-capacity-fee'
          && frozenResult?.computable === true && frozenResult.modelHash === result.modelHash
          && frozen.result.computable === true && frozen.result.modelHash === result.modelHash
          && frozen.result.modeledCapexUsd === result.modeledCapexUsd));
      if (identityMatches && result.computable && result.modeledCapexUsd != null) {
        replace('finance.capexTotalUsd', {
          value: result.modeledCapexUsd,
          status: 'estimated',
          resolvedFromTier: 2,
          sources: [{ tier: 2, kind: 'manual',
            ref: `derived:finance.capexTotalUsd(data-center-model:${result.modelHash})`,
            label: `Saved data-center model: priced BOM ${model.pricedBomUsd} USD + ${model.contingencyPct}% contingency`,
            value: result.modeledCapexUsd }],
          basis: `Modeled project CAPEX in constant ${model.priceYear} USD; schema-valid saved annual model ${result.modelHash}. Not an independently verified EPC quote; unpriced scopes and model limitations remain applicable.`,
        });
      }
    }
  }

  // The native wind datum is already an estimated resource CF (0–1).
  // Convert only that saved datum; project/LCOE assumptions never compete.
  const windFraction = readPath(root, 'sed.wind.data.capacityFactor');
  const windSource = readPath(root, 'sed.wind.source');
  const windStatus = readPath(root, 'sed.wind.status');
  if (typeof windFraction === 'number' && Number.isFinite(windFraction)
    && windFraction >= 0 && windFraction <= 1
    && typeof windSource === 'string' && windSource.trim().length > 0
    && typeof windStatus === 'string' && ['verified', 'verified-from-doc', 'verified-from-context',
      'verified-from-report', 'verified-from-research', 'verified-from-dam', 'verified-from-intent'].includes(windStatus)) {
    const percentagePoints = windFraction * 100;
    replace('wind.resourceCapacityFactorPct', {
      value: percentagePoints,
      status: 'estimated',
      resolvedFromTier: 2,
      sources: [{
        tier: 2,
        kind: windStatus === 'verified' ? 'provider' : 'site-context',
        ref: 'sed:wind.capacityFactor',
        label: `Saved wind resource: ${windSource.trim()} (fraction × 100)`,
        value: percentagePoints,
      }],
      basis: 'Estimated wind resource capacity factor from the saved wind fraction; not project/financial utilization',
    });
  }

  // A regional generator-queue median or map heuristic cannot establish this site's load-service duration.
  // Only explicitly scoped load-service evidence can supply a derived queue fact.
  const hasLoadServiceEvidence = (section: 'interconnection' | 'grid'): boolean => {
    const source = readPath(root, `sed.${section}.source`);
    const status = readPath(root, `sed.${section}.status`);
    const retrievedAt = readPath(root, `sed.${section}.retrievedAt`);
    return typeof source === 'string' && source.trim().length > 0
      && (status === 'verified' || status === 'verified-from-doc')
      && typeof retrievedAt === 'string' && Number.isFinite(Date.parse(retrievedAt));
  };
  const p50Days = readPath(root, 'sed.interconnection.data.queueDurationP50Days');
  if (hasLoadServiceEvidence('interconnection')
    && readPath(root, 'sed.interconnection.data.queueDurationBasis') === 'site-load-service'
    && typeof p50Days === 'number' && Number.isFinite(p50Days) && p50Days > 0) {
    const years = round1(p50Days / 365);
    replace('grid.queueYears', {
      value: years,
      status: 'estimated',
      resolvedFromTier: 2,
      sources: [{
        tier: 2,
        kind: readPath(root, 'sed.interconnection.status') === 'verified' ? 'provider' : 'site-context',
        ref: 'derived:grid.queueYears(queueDurationP50Days)',
        label: `Site load-service evidence (${readPath(root, 'sed.interconnection.source')}): ${p50Days} days ÷ 365`,
        value: years,
      }],
      basis: 'site-specific load-service duration estimate',
    });
  } else if (hasLoadServiceEvidence('grid')
    && readPath(root, 'sed.grid.data.estimatedQueueTimeBasis') === 'site-load-service') {
    const parsed = parseQueueTimeYears(readPath(root, 'sed.grid.data.estimatedQueueTime'));
    if (parsed) {
      replace('grid.queueYears', {
        value: parsed.years,
        status: 'estimated',
        resolvedFromTier: 2,
        sources: [{
          tier: 2,
          kind: readPath(root, 'sed.grid.status') === 'verified' ? 'provider' : 'site-context',
          ref: 'derived:grid.queueYears(estimatedQueueTime)',
          label: `Site load-service evidence (${readPath(root, 'sed.grid.source')}): "${parsed.source}" (range → midpoint)`,
          value: parsed.years,
        }],
        basis: 'parsed site load-service estimate',
      });
    }
  }

  // grid.voltageKv — parsed from the provider voltage string(s).
  const fromVoltage = parseVoltageKv(readPath(root, 'sed.grid.data.voltage'));
  const fromTransmission = fromVoltage === null
    ? parseVoltageKv(readPath(root, 'sed.grid.data.nearestTransmissionVoltage'))
    : null;
  const voltageKv = fromVoltage ?? fromTransmission;
  if (voltageKv !== null) {
    const slot = fromVoltage !== null ? 'grid.voltage' : 'grid.nearestTransmissionVoltage';
    replace('grid.voltageKv', {
      value: voltageKv,
      status: 'estimated',
      resolvedFromTier: 2,
      sources: [{
        tier: 2,
        kind: 'provider',
        ref: `derived:grid.voltageKv(${slot})`,
        label: `Derived: parsed from ${slot} (highest kV)`,
        value: voltageKv,
      }],
      basis: 'parsed provider voltage string',
    });
  }
}

/* ── Context-entity allowlist (grounding FIX 2) ───────────────────────── */

/**
 * Capitalized-token runs inside a LONG string (research prose, notes),
 * harvested per line so a phrase never crosses a newline. Permissive on the
 * ALLOWLIST side (digits / ampersands / ALL-CAPS allowed, optional of/and/&
 * connectors) — a broader allowlist only ever SUPPRESSES NER false positives.
 */
const CONTEXT_PHRASE_RE =
  /\b[A-Z][\w&.'’-]*(?:[ \t]+(?:of[ \t]+|and[ \t]+|&[ \t]+)?[A-Z][\w&.'’-]*){0,6}/g;

/** Strings longer than this are prose: harvest phrases instead of the whole. */
const CONTEXT_WHOLE_STRING_MAX = 160;
/** Hard caps so a pathological SED cannot blow up the walk. */
const CONTEXT_MAX_NODES = 30000;
const CONTEXT_MAX_ENTRIES = 4000;

/**
 * Build the NER context-entity allowlist from the site + SED: the site's own
 * name, its address components, and every entity-ish string the SED carries
 * anywhere (substation / utility / G&T / IX / reactor / pipeline-operator
 * names, county-parish-state, siteContext claim text, research-named places,
 * document titles). Without this the invented-entity NER flags the site's own
 * name and town as hallucinations (the live Redhead 192-violation storm).
 *
 * PURE + bounded (depth / node / entry caps). Never throws.
 */
export function collectContextEntities(args: {
  sed: SiteEvidenceDocument;
  site?: SiteResolverContext['site'];
}): string[] {
  const out = new Set<string>();
  const push = (raw: unknown): void => {
    if (out.size >= CONTEXT_MAX_ENTRIES) return;
    if (typeof raw !== 'string') return;
    const s = raw.trim();
    if (s.length < 2 || !/[A-Za-z]/.test(s)) return;
    out.add(s);
  };

  try {
    const site = args.site as unknown as { name?: unknown; address?: unknown } | undefined;
    push(site?.name);
    push(site?.address);
    const sedLoose = args.sed as unknown as {
      siteName?: unknown;
      location?: { address?: unknown };
    };
    push(sedLoose.siteName);
    // Address components ground the town / parish / state tokens ("Moss
    // Bluff", "Calcasieu Parish") individually.
    for (const addr of [site?.address, sedLoose.location?.address]) {
      if (typeof addr !== 'string') continue;
      push(addr);
      for (const part of addr.split(/[,;]/)) push(part);
    }

    let visited = 0;
    const walk = (node: unknown, depth: number): void => {
      if (visited >= CONTEXT_MAX_NODES || out.size >= CONTEXT_MAX_ENTRIES) return;
      visited += 1;
      if (typeof node === 'string') {
        if (node.length <= CONTEXT_WHOLE_STRING_MAX) {
          push(node);
        } else {
          // Long prose: harvest capitalized phrases line by line.
          for (const line of node.split('\n')) {
            CONTEXT_PHRASE_RE.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = CONTEXT_PHRASE_RE.exec(line)) !== null) push(m[0]);
          }
        }
        return;
      }
      if (node === null || typeof node !== 'object' || depth > 8) return;
      if (Array.isArray(node)) {
        for (const item of node) walk(item, depth + 1);
        return;
      }
      for (const value of Object.values(node as Record<string, unknown>)) walk(value, depth + 1);
    };
    walk(args.sed, 0);
  } catch {
    // Allowlist building is best-effort — a malformed SED degrades to a
    // smaller allowlist, never a crash.
  }
  return [...out];
}

/* ── The two projections ───────────────────────────────────────────────── */

/**
 * Build the `CanonicalFactStore` for a site + report type. Pure.
 */
/**
 * Fold SED amendments into the canonical facts (2026-08-05).
 *
 * `sed.amendments` is the platform's immutable human-correction channel — it
 * already survives SED rebuilds (`reapplyAmendmentsAfterRebuild`) and
 * `'user-amendment'` is already a tier-1 VERIFIED source kind. But NO
 * descriptor read it, so a human-supplied truth never reached the facts: the
 * grounding gate kept firing on a value the operator had already corrected,
 * and there was no way to answer a truth question durably.
 *
 * An amendment whose `field` names a fact overrides that fact as VERIFIED.
 * This is what makes a user's resolution persist across every downstream
 * consumer — the canonical-facts prompt block, the grounding validator, the
 * reviewer's ground truth, and every future report generated from this SED.
 *
 * TWO NAMESPACES, one bridge. Amendments address a STRUCTURAL SED path
 * (`saveSEDAmendment` writes `field: amendment.variablePath`, e.g.
 * `grid.data.utilityName`); facts carry a LOGICAL id (`electricUtility.name`).
 * Matching them directly matches almost nothing, so `sedPathToFactId()`
 * derives the mapping from the descriptor table's own `sed.`-rooted candidate
 * paths — the same declarations the builder reads values through, so the
 * bridge cannot drift from the resolution logic. A `field` is resolved as a
 * factId first, then through that index.
 *
 * Last amendment wins (they are appended chronologically). Amendments naming
 * an unknown fact are ignored here — they remain on the SED for the field-level
 * consumers that already read them.
 */
/** Format-insensitive key so `grid.data.voltage` ≡ `Grid_Data_Voltage`. */
function normAmendmentKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Lazily-built `SED path → factId` index derived from the descriptor table.
 *
 * Every slot candidate declares where its value lives (`sed.grid.data.voltage`);
 * stripping the `sed.` root yields exactly the `variablePath` an amendment
 * carries. Built once — `FACT_DESCRIPTORS` is a frozen module constant.
 *
 * When two descriptors claim the same SED path the FIRST wins. Order in the
 * table is deliberate (winner-first), and the alternative — last-wins — would
 * silently hand a shared path to whichever descriptor happened to be appended
 * most recently.
 */
let sedPathIndexCache: Map<string, string> | null = null;
export function sedPathToFactId(): Map<string, string> {
  if (sedPathIndexCache) return sedPathIndexCache;
  const index = new Map<string, string>();
  for (const descriptor of FACT_DESCRIPTORS) {
    for (const candidate of descriptor.candidates) {
      if (!candidate.path.startsWith('sed.')) continue;
      const key = normAmendmentKey(candidate.path.slice('sed.'.length));
      if (!index.has(key)) index.set(key, descriptor.factId);
    }
  }
  sedPathIndexCache = index;
  return index;
}

/**
 * `factId → SED variablePath` — the inverse bridge, for WRITING.
 *
 * A truth question names a fact, but an amendment must address a real SED
 * location: `applyAmendmentToSed` refuses any path that neither exists on the
 * document nor appears in the binding registry, precisely so a hallucinated
 * path can never invent a value somewhere wrong. Resolving the fact to its
 * OWN tier-1 slot path makes an operator's answer an ordinary, well-formed
 * SED amendment — which is what carries it into the Variable Browser,
 * revisions, and `reapplyAmendmentsAfterRebuild`.
 *
 * Tier-1 preferred: that slot is the authoritative location for the fact, and
 * writing there is what displaces the provider value the report disputed.
 * Returns undefined for facts with no SED-rooted slot (claim/text-derived
 * only) — the caller must not fabricate a path for those.
 */
export function factIdToSedPath(factId: string): string | undefined {
  const target = normAmendmentKey(factId);
  for (const descriptor of FACT_DESCRIPTORS) {
    if (normAmendmentKey(descriptor.factId) !== target) continue;
    const sedSlots = descriptor.candidates.filter((c) => c.path.startsWith('sed.'));
    const preferred = sedSlots.find((c) => c.tier === 1) ?? sedSlots[0];
    return preferred?.path.slice('sed.'.length);
  }
  return undefined;
}

/**
 * The display unit a governed fact is stored in, for validating typed input.
 *
 * The truth-resolution dialog writes an operator's answer into this fact's SED
 * field, so it must know the field's unit to convert "1.9 miles" into the
 * kilometres the field declares — and to REFUSE a value whose unit belongs to
 * another dimension. Undefined for entity/categorical facts, which take no
 * numeric input.
 */
export function factIdToUnit(factId: string): string | undefined {
  const target = normAmendmentKey(factId);
  for (const descriptor of FACT_DESCRIPTORS) {
    if (normAmendmentKey(descriptor.factId) === target) return descriptor.unit;
  }
  return undefined;
}

export function applyUserAmendmentFacts(
  facts: CanonicalFact[],
  sed: SiteEvidenceDocument | null | undefined,
): void {
  const amendments = sed?.amendments;
  if (!Array.isArray(amendments) || amendments.length === 0) return;
  const byKey = new Map<string, number>();
  facts.forEach((fact, index) => byKey.set(normAmendmentKey(fact.factId), index));
  const sedPaths = sedPathToFactId();

  for (const amendment of amendments) {
    const field = typeof amendment?.field === 'string' ? amendment.field : '';
    if (!field) continue;
    const key = normAmendmentKey(field);
    // factId first (a truth-question answer addresses the fact directly),
    // then the structural SED path an ordinary variable amendment carries.
    const mappedFactId = sedPaths.get(key);
    const index = byKey.get(key)
      ?? (mappedFactId === undefined ? undefined : byKey.get(normAmendmentKey(mappedFactId)));
    if (index === undefined) continue;
    const corrected = amendment.correctedValue;
    if (corrected === undefined) continue;

    const existing = facts[index]!;
    facts[index] = {
      ...existing,
      value: corrected as CanonicalFact['value'],
      // The spread carries `existing`'s band forward; an operator's scalar
      // answer REPLACES the declared range, so the band must be cleared or a
      // stale allow-value keeps grounding prose the correction just overrode.
      numericValues: undefined,
      bandDisplay: undefined,
      // A human answer is authoritative — never 'estimated' / 'absent'.
      status: corrected === null ? 'absent' : 'verified',
      sources: [{
        kind: 'user-amendment',
        tier: 1,
        label: amendment.source?.trim()
          || `User correction${amendment.correctedBy ? ` (${amendment.correctedBy})` : ''}`,
        ref: `amendment:${field}`,
        value: corrected as never,
      } as CanonicalFact['sources'][number]],
      // The correction settles the question — stale conflicts must not
      // resurface a value the operator has explicitly overruled.
      conflicts: [],
    };
  }
}

export function buildCanonicalFactStore(args: BuildCanonicalFactStoreArgs): CanonicalFactStore {
  const nowIso = args.nowIso ?? new Date().toISOString();
  const coverageMode: CanonicalFactStore['coverageMode'] =
    args.coverageMode ?? args.sed.coverageMode ?? 'US';

  const resolved = buildResolvedLookup(args.ctx);
  const root: Record<string, unknown> = {
    sed: args.sed,
    // Report-stamped candidate slots (`report.` paths) resolve when the ctx
    // carries a report; at prompt-build time there is none and they skip.
    report: args.ctx.reports?.[0],
    providerData: args.providerData ?? {},
    // Authored report-setup values for the selected type (`prefs.` paths).
    prefs: args.typedPrefsForType ?? {},
    // Live intent is the generation authority. Fall back to the SED-frozen
    // full intent for legacy/server paths whose site entity lacks it.
    intent: args.ctx.site.projectIntent ?? args.sed.fullProjectIntent,
    resolved,
  };

  const facts: CanonicalFact[] = FACT_DESCRIPTORS.map((descriptor) =>
    resolveDescriptor(descriptor, root, coverageMode, nowIso),
  );

  // PeeringDB may return a colocation facility when the IX query fails.
  // Its geographic distance is not an IX fact. Human amendments below still
  // retain their existing authority.
  if (args.sed.connectivity?.data?.nearestIxKind === 'colo-fallback') {
    const index = facts.findIndex(fact => fact.factId === 'ix.distanceKm');
    if (index >= 0) {
      const fallback = facts[index]!;
      if (fallback.status !== 'absent') {
        facts.push({
          ...fallback, factId: 'colocation.distanceKm', label: 'Nearest colocation facility (geographic proximity)',
          sources: fallback.sources.map(source => ({ ...source, label: 'PeeringDB colocation facility fallback' })),
          basis: 'Colocation inventory proximity; not an IX, surveyed fiber route or measured latency',
        });
      }
      facts[index] = { ...fallback, value: null, status: 'absent', resolvedFromTier: null, sources: [], basis: 'Colocation facility fallback; actual IX distance unresolved' };
    }
  }

  // Derived FILLS for scoring facts whose slots need conversion/parsing
  // (fill-when-absent; slot/claim resolutions always win).
  applyScoringDerivedFacts(facts, root, { reportType: args.reportType, siteId: args.ctx.site.id,
    ownerUid: args.ctx.site._ownerUid ?? args.ctx.site.createdBy?.uid });

  // Conditional screening loads have their own role-scoped facts. They must
  // never replace the confirmed (possibly absent) power boundary or supply.
  const economic = args.sed.economicEvidence?.data;
  const waterScreens = [economic?.waterUseScreening, ...(economic?.waterUseScenarioScreens ?? []).map(item => item.result)];
  for (const [caseIndex, screen] of waterScreens.entries()) {
    if (screen?.status !== 'calculated' || !screen.resolvedContext) continue;
    if (screen.loadRange.basis !== 'it' && screen.loadRange.basis !== 'facility') continue;
    const phase = args.ctx.site.projectIntent?.phases?.find(item => item.id === screen.loadRange.phaseBasis);
    for (const [metric, range] of [['it', screen.resolvedContext.itLoadMw], ['facility', screen.resolvedContext.facilityHeatMw]] as const) {
      const metricLabel = metric === 'it' ? 'Net IT' : 'Gross Facility';
      facts.push({
        factId: `screeningLoad.${caseIndex}.${metric}Mw`, label: `Conditional ${phase ? `Phase ${phase.sequence}` : 'whole-project'} ${metricLabel} load`,
        value: `${range.low}–${range.high}`, numericValues: [range.low, range.high],
        unit: 'MW', valueKind: 'numeric', status: 'estimated', resolvedFromTier: 2,
        sources: [{ tier: 2, kind: 'site-context', ref: `sed:economicEvidence.water-screen:${caseIndex}`,
          label: 'Deterministic conditional screening calculation, not confirmed demand or generation' }],
        basis: `Conditional ${screen.loadRange.basis === 'it' ? 'Net IT' : 'Gross Facility'} input case; ${metricLabel} load; PUE ${screen.resolvedContext.pue.low}–${screen.resolvedContext.pue.high}`,
        numericGroundingScope: { kind: 'screening-load', metric, inputBasis: screen.loadRange.basis,
          ...(phase ? { phaseSequence: phase.sequence } : {}) },
      });
    }
  }

  // Derived facts (computed, not slot-backed; no same-id descriptor exists).
  facts.push(...buildDerivedFacts(root));

  // DECLARED-BAND fill for range-bucket intents. Runs before amendments so an
  // operator's exact answer replaces the band rather than competing with it.
  applyDeclaredBandFact(facts, args.sed, args.ctx.site?.projectIntent);

  // Fibre quantities from the user's CAD layers — see applyFiberCorridorFacts.
  applyFiberCorridorFacts(facts, args.sed);


  // USER TRUTH RESOLUTIONS (2026-08-05) — a human answer WINS.
  applyUserAmendmentFacts(facts, args.sed);

  // Tag each surfaced conflict with the owning factId so the prompt + audit
  // can attribute it. `reconcileTierSources` stamps `fieldKey === factId`
  // already; this is a defensive normalisation only.
  for (const fact of facts) {
    if (fact.conflicts) {
      fact.conflicts = fact.conflicts.map((c: ConflictDetail) => ({ ...c, fieldKey: fact.factId }));
    }
  }

  return createCanonicalFactStore({
    siteId: args.ctx.site.id,
    reportType: args.reportType,
    coverageMode,
    facts,
    // Grounding FIX 2 — the NER context-entity allowlist (site name, address
    // components, every entity-ish SED string). Suppression-only.
    contextEntities: collectContextEntities({ sed: args.sed, site: args.ctx.site }),
  });
}

/* ── Scoring slice (pillar scorer projection) ──────────────────────────── */

export interface BuildScoringFactSliceArgs {
  /** The SED whose slots + siteContext claims back the facts. Optional. */
  sed?: SiteEvidenceDocument | null;
  /**
   * The report being scored — supplies the report-stamped candidate slots
   * (`financialAssumptionsAtGen`, overrides) and the frozen
   * `siteContextSnapshot` claims (preferred over the live SED claims for
   * reproducibility). Optional.
   */
  report?: SiteRagReport | null;
  /** Coverage mode override; defaults to `sed.coverageMode ?? 'US'`. */
  coverageMode?: SiteEvidenceDocument['coverageMode'];
  /** Deterministic clock for tests. */
  nowIso?: string;
}

/**
 * Resolve ONLY the scoring-critical facts (`SCORING_FACT_IDS`) against a
 * `{ sed, report }` root — no registry manifest / site entity required, so
 * the portfolio pillar scorer can call this with exactly the inputs it
 * already has. Same descriptors, same reconciliation, same status honesty as
 * the full store; `resolved.*` candidates simply do not compete (their root
 * is empty).
 *
 * PURE + total: with neither a SED nor a report every fact resolves ABSENT.
 */
export function buildScoringFactSlice(
  args: BuildScoringFactSliceArgs,
): Map<string, CanonicalFact> {
  const nowIso = args.nowIso ?? new Date().toISOString();
  const coverageMode: CanonicalFactStore['coverageMode'] =
    args.coverageMode ?? args.sed?.coverageMode ?? 'US';

  const root: Record<string, unknown> = {
    sed: args.sed ?? {},
    report: args.report ?? undefined,
    providerData: {},
    resolved: {},
  };

  const scoringIds = new Set(SCORING_FACT_IDS);
  const facts: CanonicalFact[] = FACT_DESCRIPTORS
    .filter((d) => scoringIds.has(d.factId))
    .map((descriptor) => resolveDescriptor(descriptor, root, coverageMode, nowIso));

  applyScoringDerivedFacts(facts, root, { reportType: args.report?.reportType,
    siteId: args.report?.siteId ?? args.sed?.financialAssumptions?.siteId, ownerUid: args.report?._ownerUid });

  const out = new Map<string, CanonicalFact>();
  for (const fact of facts) out.set(fact.factId, fact);
  return out;
}
